import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths } from "date-fns";
import { fr } from "date-fns/locale";

export const useProfitComparison = (period: string) => {
  const getPeriodDates = (date: Date) => {
    switch (period) {
      case "day":
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
      case "week":
        return {
          start: startOfWeek(date, { locale: fr }),
          end: endOfWeek(date, { locale: fr })
        };
      case "month":
        return {
          start: startOfMonth(date),
          end: endOfMonth(date)
        };
      default:
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
    }
  };

  const { data } = useQuery({
    queryKey: ["profit_comparison", period],
    queryFn: async () => {
      const now = new Date();
      const currentPeriod = getPeriodDates(now);
      
      let previousDate;
      switch (period) {
        case "day":
          previousDate = subDays(now, 1);
          break;
        case "week":
          previousDate = subWeeks(now, 1);
          break;
        case "month":
          previousDate = subMonths(now, 1);
          break;
        default:
          previousDate = subDays(now, 1);
      }
      const previousPeriod = getPeriodDates(previousDate);

      const fetchPeriodData = async (start: Date, end: Date) => {
        // Récupérer les revenus validés
        const { data: revenues, error: revenuesError } = await supabase
          .from("validated_amounts")
          .select("amount")
          .eq("status", "validated")
          .gte("counting_date", start.toISOString().split('T')[0])
          .lte("counting_date", end.toISOString().split('T')[0]);

        if (revenuesError) throw revenuesError;

        // Récupérer les dépenses
        const { data: expenses, error: expensesError } = await supabase
          .from("poultry_transactions")
          .select("total_amount")
          .eq("transaction_type", "expense")
          .gte("created_at", start.toISOString())
          .lte("created_at", end.toISOString());

        if (expensesError) throw expensesError;

        // Calculer les totaux
        const totalRevenues = revenues?.reduce((sum, record) => sum + record.amount, 0) || 0;
        const totalExpenses = expenses?.reduce((sum, record) => sum + record.total_amount, 0) || 0;
        const netProfit = totalRevenues - totalExpenses;

        return {
          revenues: totalRevenues,
          expenses: totalExpenses,
          netProfit
        };
      };

      const current = await fetchPeriodData(currentPeriod.start, currentPeriod.end);
      const previous = await fetchPeriodData(previousPeriod.start, previousPeriod.end);

      console.log("Current period data:", current);
      console.log("Previous period data:", previous);

      return {
        currentPeriod: current,
        previousPeriod: previous
      };
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  return {
    currentPeriod: data?.currentPeriod || { revenues: 0, expenses: 0, netProfit: 0 },
    previousPeriod: data?.previousPeriod || { revenues: 0, expenses: 0, netProfit: 0 }
  };
};